import { useMutation } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';

import { cikis } from '../api/kimlik';
import { oturumDeposuKullan } from '../durum/oturum-deposu';
import { yetkiKullan, type YetkiKodu } from '../yetki/yetki';
import { BildirimRozeti } from './BildirimRozeti';

/**
 * PANEL KABUĞU.
 *
 * Menü kullanıcının yetkilerine göre kurulur. BU BİR GÜVENLİK DENETİMİ
 * DEĞİLDİR — sunucu her istekte yeniden denetler; buradaki filtre yalnız
 * kullanıcıyı erişemeyeceği ekrandan korur.
 *
 * Yer tutucu bağlantı EKLENMEZ: tıklanınca boş ekran açan bir menü, olmayan
 * menüden kötüdür. Listedeki her satırın karşılığı `Uygulama.tsx`te bağlı.
 */

interface MenuOgesi {
  etiket: string;
  yol: string;
  /** Bu yetkilerden en az biri gerekir. Boş = herkese açık. */
  yetkiler: YetkiKodu[];
}

/**
 * MENÜ YETKİLERİ UÇLARIN `@Yetki` LİSTESİYLE HİZALI olmalı.
 *
 * Menüde daha geniş bir kod yazılırsa kullanıcı ekranı açar ve içerideki
 * istek 403 döner — boş tablo ve anlaşılmaz bir hata. Daha dar yazılırsa
 * erişebileceği ekranı hiç göremez.
 */
const MENU: MenuOgesi[] = [
  {
    etiket: 'Birim ağacı',
    yol: '/birim-agaci',
    // `GET /organizasyon/birim-agaci` üç kod kabul ediyor; `SISTEM_YONET`
    // burada eksikti ve yalnız o koda sahip kullanıcı erişebildiği ekranı
    // hiç göremiyordu.
    yetkiler: ['BIRIM_YONET', 'PERSONEL_GORUNTULE', 'SISTEM_YONET'],
  },
  { etiket: 'Personel', yol: '/personel', yetkiler: ['PERSONEL_GORUNTULE', 'PERSONEL_YONET'] },
  {
    etiket: 'Atamalar',
    yol: '/atamalar',
    /*
     * `ATAMA_YAP` talep eder, `ATAMA_ONAYLA` karara bağlar — görevler
     * ayrılığı. `PERSONEL_GORUNTULE` yalnız listeyi görür.
     */
    yetkiler: ['PERSONEL_GORUNTULE', 'ATAMA_YAP', 'ATAMA_ONAYLA'],
  },
  {
    etiket: 'Vardiya planı',
    yol: '/vardiya-plani',
    yetkiler: [
      'PERSONEL_GORUNTULE',
      'VARDIYA_PLAN_URET',
      'VARDIYA_PLAN_DUZELT',
      'VARDIYA_PLAN_ONAYLA',
    ],
  },
  {
    etiket: 'Puantaj denetimi',
    yol: '/puantaj-denetimi',
    // İki sekmenin BİRLEŞİMİ: `/puantaj/denetim` → `DENETIM_EKRANI`,
    // `/puantaj/kota` → `DENETIM_EKRANI | MESAI_ON_IZIN_ONAY |
    // RAPOR_GORUNTULE`. Ekran içinde her sekme kendi yetkisine bakıyor.
    yetkiler: ['DENETIM_EKRANI', 'MESAI_ON_IZIN_ONAY', 'RAPOR_GORUNTULE'],
  },
  {
    etiket: 'Mevzuat ve Rehber',
    yol: '/rehber',
    // YETKİSİZ — rehberin OKUMA uçlarının hiçbiri `@Yetki` taşımıyor.
    // Gerekçe kontrolcüde yazılı: sistem personelin ücretini hesaplıyor,
    // "bu tutar hangi kurala göre çıktı" sorusuna cevap bulabilmek itiraz
    // hakkının ön koşulu. Ekrandaki Doğrulama sekmesi ayrıca yetkili.
    yetkiler: [],
  },
  {
    etiket: 'Görevler',
    yol: '/gorev',
    /*
     * DÖRT SEKMENİN BİRLEŞİMİ. `MESAI_KAYDI_BAS` seed'de TÜM pozisyonlara
     * verilmiş, yani menü fiilen herkese açık — ama günlük görev ucu
     * `birimErisiminiDogrula` ile kapsam kontrolü yapıp 403 dönüyor ve
     * sekmeler kendi yetkilerine bakıyor.
     */
    yetkiler: [
      'PERSONEL_GORUNTULE',
      'GOREV_OLUSTUR',
      'GOREV_TIPI_YONET',
      'MESAI_KAYDI_BAS',
      'RAPOR_GORUNTULE',
    ],
  },
  {
    etiket: 'Mesai ön izni',
    yol: '/mesai-on-izni',
    /*
     * ÜÇ SEKMENİN BİRLEŞİMİ. Talep `MESAI_ON_IZIN_TALEP` (şef), onay
     * `MESAI_ON_IZIN_ONAY` (müdür) — 4857/41 gereği ayrı kişiler. Rapor
     * `DENETIM_EKRANI`yi de kabul ediyor.
     */
    yetkiler: ['MESAI_ON_IZIN_TALEP', 'MESAI_ON_IZIN_ONAY', 'RAPOR_GORUNTULE', 'DENETIM_EKRANI'],
  },
  {
    etiket: 'İzin',
    yol: '/izin',
    /*
     * ÜÇ SEKMENİN BİRLEŞİMİ: liste `PERSONEL_GORUNTULE | IZIN_ONAY_ILK |
     * IZIN_ONAY_SON`, onay `IZIN_ONAY_ILK | IZIN_ONAY_SON`, kendi izinlerim
     * `IZIN_TALEP`. Son kod her pozisyona `kendi` kapsamıyla veriliyor ve
     * KISITLANAMAZ (4857/53) — yani menü fiilen herkese açık, ama sekmeler
     * ve düğmeler kendi yetkilerine bakıyor.
     */
    yetkiler: ['PERSONEL_GORUNTULE', 'IZIN_ONAY_ILK', 'IZIN_ONAY_SON', 'IZIN_TALEP'],
  },
  {
    etiket: 'Dönem kapanışı',
    yol: '/donem',
    /*
     * ÜÇ SEKMENİN BİRLEŞİMİ: dönem listesi `PERSONEL_GORUNTULE | DONEM_KAPAT |
     * RAPOR_GORUNTULE`, mutabakat `MUTABAKAT_GIR`i de kabul ediyor, itiraz
     * listesi `ITIRAZ_KARAR`ı. Sekmeler ve düğmeler kendi uçlarının
     * listesine bakıyor.
     */
    yetkiler: [
      'PERSONEL_GORUNTULE',
      'DONEM_KAPAT',
      'RAPOR_GORUNTULE',
      'MUTABAKAT_GIR',
      'ITIRAZ_KARAR',
    ],
  },
  {
    etiket: 'Kullanıcılar',
    yol: '/kullanicilar',
    /*
     * `SISTEM_YONET` rol verir ve hesap kapatır; `PERSONEL_YONET` hesap açar
     * ve şifre sıfırlar. İkincisi menüyü görür ama rol düğmelerini görmez —
     * rol yetki demektir ve kendi kendini genişletememeli.
     */
    yetkiler: ['SISTEM_YONET', 'PERSONEL_YONET'],
  },
  {
    etiket: 'Ayarlar ve Takvim',
    yol: '/ayarlar',
    /*
     * BEŞ UÇ, BEŞ FARKLI LİSTE — menü hepsinin BİRLEŞİMİ.
     * `GET /yonetim/takvim` `PERSONEL_GORUNTULE` kabul ediyor; menü ona
     * kapatılsaydı ekip lideri erişebildiği ekranı hiç göremezdi (§11).
     * Sekmeler ve yazma düğmeleri ekran içinde kendi yetkilerine bakıyor.
     */
    yetkiler: [
      'PARAMETRE_YONET',
      'SISTEM_YONET',
      'RAPOR_GORUNTULE',
      'DONEM_KAPAT',
      'PERSONEL_GORUNTULE',
    ],
  },
  { etiket: 'Cihazlarım', yol: '/cihazlarim', yetkiler: [] },
];

export function Kabuk() {
  const kullanici = oturumDeposuKullan((d) => d.kullanici);
  const etkinAtama = oturumDeposuKullan((d) => d.etkinAtama);
  const dustu = oturumDeposuKullan((d) => d.dustu);

  const cikisYap = useMutation({
    mutationFn: cikis,
    // Sunucu hata verse bile yerel oturum kapatılır: kullanıcı "çıkış yaptım"
    // sanıp ekranı açık bırakırsa panel erişilebilir kalırdı.
    onSettled: () => dustu(),
  });

  return (
    <div className="flex h-full">
      <aside className="hidden w-60 shrink-0 border-r border-slate-200 bg-white md:block">
        <div className="border-b border-slate-200 px-4 py-4">
          <p className="text-sm font-semibold text-slate-900">e-alanya</p>
          <p className="text-xs text-slate-500">Yönetim Paneli</p>
        </div>

        <nav className="p-2">
          {MENU.map((oge) => (
            <MenuBaglantisi key={oge.yol} oge={oge} />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">
              {kullanici ? `${kullanici.ad} ${kullanici.soyad}` : '—'}
            </p>
            <p className="truncate text-xs text-slate-500">
              {etkinAtama ? `${etkinAtama.birimAd} · ${etkinAtama.pozisyonKod}` : 'Aktif atama yok'}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <BildirimRozeti />
            <button
              type="button"
              onClick={() => cikisYap.mutate()}
              disabled={cikisYap.isPending}
              className="rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Çıkış
            </button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function MenuBaglantisi({ oge }: { oge: MenuOgesi }) {
  // Kanca koşulsuz çağrılır; koşullu çağrı React kurallarını ihlal eder.
  const yetkili = yetkiKullan(...oge.yetkiler);

  if (oge.yetkiler.length > 0 && !yetkili) return null;

  return (
    <NavLink
      to={oge.yol}
      className={({ isActive }) =>
        `block rounded-md px-3 py-2 text-sm ${
          isActive ? 'bg-kurum-50 font-medium text-kurum-700' : 'text-slate-700 hover:bg-slate-100'
        }`
      }
    >
      {oge.etiket}
    </NavLink>
  );
}
